/**
 * TreeSketchPreview — the "try before signup" tree sketch shown on
 * /family-trees/new. User types a natural-language description of
 * their close relatives; we POST to /api/family-trees/preview which
 * returns a normalised {members, relationships} graph; we render it
 * as a compact SVG family tree.
 *
 * This component is intentionally self-contained: no auth calls, no
 * writes, no dependency on the rest of the family-tree infra. It's a
 * marketing surface that happens to render real data — so if the LLM
 * or backend is down it still shows a sane fallback tree.
 */
import { useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, TreePine } from "lucide-react";

const EXAMPLE_PROMPTS = [
  "My father is Ravi Kumar and my mother is Priya. I have a younger sister named Meera and a brother Arjun. My grandfather is Krishna.",
  "My dad is Anil, my mom is Sunita. I have a sister Anjali. My paternal grandfather is Ram Prasad and grandmother is Kamala.",
  "My father is George Kurien from Pala, my mother is Molly. I have a brother Thomas.",
];

// Colours tuned to the IFT saffron/parchment palette so this widget
// feels native on /family-trees/new when the site is IFT — but stays
// neutral enough for the other sister apps.
const PALETTE = {
  card:     "#FFFDF8",
  border:   "#E7D8B7",
  divider:  "#8A3F22",
  ink:      "#2E1A05",
  saffron:  "#FF9933",
  maleBg:   "#DDECFB",
  femaleBg: "#FDE1EC",
  neutralBg:"#EDE7DA",
  link:     "#A44A21",
};

/* ── SVG tree renderer ─────────────────────────────────────────── */

function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] || "").join("").toUpperCase() || "?";
}

function TreeSVG({ members, relationships }) {
  // Group by generation and lay out each row centred within a fixed
  // canvas. Anything with more than 5 per row scales down node size.
  const layout = useMemo(() => {
    const byGen = {};
    for (const m of members) {
      const g = Number.isFinite(m.generation) ? m.generation : 0;
      (byGen[g] ||= []).push(m);
    }
    const gens = Object.keys(byGen).map(Number).sort((a, b) => a - b);
    const w = 560, h = 360;
    const rowGap = gens.length > 1 ? (h - 80) / (gens.length - 1) : 0;
    const positions = {};
    gens.forEach((g, gi) => {
      const row = byGen[g];
      const spacing = w / (row.length + 1);
      row.forEach((m, i) => {
        positions[m.id] = { x: spacing * (i + 1), y: 40 + gi * rowGap, gen: g };
      });
    });
    return { positions, w, h };
  }, [members]);

  const nodeR = 22;
  const spouseLines = relationships.filter((r) => r.type === "spouse");
  const parentLines = relationships.filter((r) => r.type === "parent");

  return (
    <svg
      viewBox={`0 0 ${layout.w} ${layout.h}`}
      className="w-full h-auto rounded-xl"
      style={{ background: PALETTE.card, border: `1px solid ${PALETTE.border}` }}
      data-testid="tree-sketch-svg"
    >
      {/* subtle parchment grain */}
      <defs>
        <pattern id="grain" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.35" fill={PALETTE.border} opacity="0.3" />
        </pattern>
      </defs>
      <rect x="0" y="0" width={layout.w} height={layout.h} fill="url(#grain)" opacity="0.6" />

      {/* parent→child bezier lines */}
      {parentLines.map((r, i) => {
        const p = layout.positions[r.parent], c = layout.positions[r.child];
        if (!p || !c) return null;
        const midY = (p.y + c.y) / 2;
        return (
          <path
            key={`p-${i}`}
            d={`M${p.x},${p.y + nodeR} C${p.x},${midY} ${c.x},${midY} ${c.x},${c.y - nodeR}`}
            fill="none"
            stroke={PALETTE.divider}
            strokeOpacity="0.55"
            strokeWidth="1.5"
          />
        );
      })}

      {/* spouse horizontal lines */}
      {spouseLines.map((r, i) => {
        const a = layout.positions[r.a], b = layout.positions[r.b];
        if (!a || !b) return null;
        return (
          <line
            key={`s-${i}`}
            x1={a.x + nodeR - 2} y1={a.y}
            x2={b.x - nodeR + 2} y2={b.y}
            stroke={PALETTE.saffron}
            strokeWidth="2"
            strokeDasharray="4 3"
          />
        );
      })}

      {/* nodes */}
      {members.map((m) => {
        const pos = layout.positions[m.id];
        if (!pos) return null;
        const bg =
          m.name === "You" ? PALETTE.saffron :
          m.gender === "M" ? PALETTE.maleBg :
          m.gender === "F" ? PALETTE.femaleBg :
                             PALETTE.neutralBg;
        return (
          <g key={m.id} data-testid={`tree-node-${m.id}`}>
            <circle
              cx={pos.x}
              cy={pos.y}
              r={nodeR}
              fill={bg}
              stroke={PALETTE.divider}
              strokeOpacity="0.4"
              strokeWidth="1.2"
            />
            <text
              x={pos.x}
              y={pos.y + 4}
              textAnchor="middle"
              fontSize="11"
              fontWeight="700"
              fontFamily="'Playfair Display', Georgia, serif"
              fill={m.name === "You" ? "#FFFFFF" : PALETTE.ink}
            >
              {initialsOf(m.name)}
            </text>
            <text
              x={pos.x}
              y={pos.y + nodeR + 12}
              textAnchor="middle"
              fontSize="10"
              fontWeight="600"
              fontFamily="'Playfair Display', Georgia, serif"
              fill={PALETTE.ink}
            >
              {(m.name || "").length > 16 ? (m.name.slice(0, 15) + "…") : m.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Main component ───────────────────────────────────────────── */

export default function TreeSketchPreview({ onSignupNudge }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {members, relationships, source}
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = prompt.trim();
    if (text.length < 6) {
      toast.error("Type a few relatives first — e.g. 'my father is Ravi, my mother is Priya'.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      const { data } = await http.post("/family-trees/preview", { prompt: text });
      setResult(data);
      if (data?.source === "fallback") {
        // Not a hard error — but let the user know the AI is asleep so
        // the sketch will look simpler.
        toast("Sketching with the offline parser — sign in to fine-tune.", { icon: "ℹ️" });
      }
    } catch (e2) {
      const status = e2?.response?.status;
      if (status === 429) {
        setErr("Too many previews from this device. Try again in a few minutes.");
      } else {
        setErr(e2?.response?.data?.detail || "Preview failed. Please try again.");
      }
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-2xl border p-4 sm:p-5"
      style={{ borderColor: PALETTE.border, background: PALETTE.card }}
      data-testid="tree-sketch-preview"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="w-5 h-5 mt-0.5" style={{ color: PALETTE.saffron }} aria-hidden />
        <div className="flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: PALETTE.link }}>
            Try it — no signup
          </div>
          <h2
            className="mt-1 font-display text-xl sm:text-2xl leading-tight"
            style={{ color: PALETTE.ink, fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            See your family tree in seconds.
          </h2>
          <p className="mt-1 text-sm" style={{ color: "#4A2A0F" }}>
            Type a few relatives — parents, siblings, grandparents. We'll
            sketch a preview so you can see how it will look. Sign in to
            save it and invite family.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-4">
        <textarea
          rows={3}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="My father is Ravi Kumar, my mother is Priya. I have a sister Meera and brother Arjun. My grandfather is Krishna."
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none resize-y"
          style={{ borderColor: PALETTE.border, background: "#FFFFFF", color: PALETTE.ink }}
          maxLength={1200}
          data-testid="tree-sketch-prompt"
        />
        <div className="mt-2 flex flex-wrap gap-1">
          {EXAMPLE_PROMPTS.map((ex, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPrompt(ex)}
              className="text-[11px] rounded-full border px-2 py-1 hover:opacity-80 transition-opacity"
              style={{ borderColor: PALETTE.border, color: PALETTE.link, background: "#FFFFFF" }}
              data-testid={`tree-sketch-example-${i}`}
            >
              Example {i + 1}
            </button>
          ))}
        </div>
        <button
          type="submit"
          disabled={busy}
          className="mt-3 group inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors"
          style={{ background: PALETTE.saffron, color: PALETTE.ink }}
          data-testid="tree-sketch-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy ? "Sketching…" : "Sketch my tree"}
        </button>
      </form>

      {err && (
        <div
          className="mt-3 rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "#F0C4C4", background: "#FEF2F2", color: "#7F1D1D" }}
          data-testid="tree-sketch-error"
        >
          {err}
        </div>
      )}

      {result && result.members?.length > 0 && (
        <div className="mt-5" data-testid="tree-sketch-result">
          <TreeSVG members={result.members} relationships={result.relationships || []} />

          <div
            className="mt-4 rounded-xl px-4 py-4 text-center"
            style={{ background: "#FEF9EE", border: `1px solid ${PALETTE.border}` }}
          >
            <p className="text-sm" style={{ color: PALETTE.ink }}>
              Like it? Sign in to save this tree and invite family to fill
              in the missing branches together.
            </p>
            <button
              type="button"
              onClick={() => onSignupNudge?.(prompt)}
              className="mt-3 group inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold"
              style={{ background: PALETTE.saffron, color: PALETTE.ink }}
              data-testid="tree-sketch-signup-cta"
            >
              <TreePine className="w-4 h-4" aria-hidden />
              Sign in & save my tree
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
